import { cn } from "@/lib/utils";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, fetchScoreboard } from "../../api";
import type { Scoreboard, ScoreboardPlayer, ScoreboardTeam } from "../../types";
import { ddFallback, getChampionIconUrl, getQueueLabel, getRuneIconUrl, getRuneName, getSummonerSpellName, getSummonerSpellUrl } from "../../utils/ddragon";
import { summonerPath } from "../../utils/regions";
import { button, Skeleton } from "../ui/primitives";
import { formatDuration, formatKda, ItemIcon, kdaColor, kdaOf } from "./parts";

const k = (n: number) => `${(n / 1000).toFixed(1)}k`;
const WIDE_COLS = "grid-cols-[minmax(140px,1.3fr)_76px_minmax(90px,1fr)_44px_48px_36px_166px] gap-2";

function TeamHeader({ team, side, v }: { team: ScoreboardTeam; side: "blue" | "red"; v: string }) {
	const o = team.objectives;
	return (
		<div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 bg-raised px-3 py-2.5 text-xs text-ink-2">
			<span className="flex items-center gap-[7px] text-[13px] font-extrabold text-ink">
				<span aria-hidden="true" className={cn("h-2 w-2 rounded-[2px]", side === "blue" ? "bg-side-blue" : "bg-side-red")} />
				{side === "blue" ? "Blue side" : "Red side"}
			</span>
			<span className={cn("font-bold", team.win ? "text-signal" : "text-ink-2")}>{team.win ? "Victory" : "Defeat"}</span>
			<span className="font-mono">
				{team.kills} / {team.deaths} / {team.assists}
			</span>
			<span>{k(team.gold)} gold</span>
			<span className="text-ink-3">
				Towers {o.towers} · Dragons {o.dragons} · Barons {o.barons} · Heralds {o.heralds} · Grubs {o.grubs}
			</span>
			{team.bans.length > 0 && (
				<span className="ml-auto flex items-center gap-[3px]">
					<span className="mr-[3px] text-ink-3">Bans</span>
					{team.bans.map((id, i) => (
						<img key={i} src={getChampionIconUrl(id, v)} onError={ddFallback} alt="" loading="lazy" className="h-[18px] w-[18px] rounded grayscale opacity-70" />
					))}
				</span>
			)}
		</div>
	);
}

function PlayerName({ p, region, isMe, className }: { p: ScoreboardPlayer; region: string; isMe: boolean; className?: string }) {
	if (!p.name) return <span className={cn("block truncate text-ink-3", className)}>Unknown</span>;
	return (
		<Link to={summonerPath(region, p.name, p.tagLine)} className={cn("block truncate hover:underline", isMe ? "font-extrabold" : "font-semibold", className)}>
			{p.name}
		</Link>
	);
}

function ChampWithLevel({ p, size, v }: { p: ScoreboardPlayer; size: number; v: string }) {
	return (
		<span className="relative shrink-0">
			<img src={getChampionIconUrl(p.championId, v)} onError={ddFallback} alt={p.championName} loading="lazy" style={{ width: size, height: size }} className="block rounded-md" />
			<span className="absolute -right-1 -bottom-1 rounded border border-line-2 bg-page px-[3px] text-[9px] font-bold leading-[13px]" aria-label={`Level ${p.level}`}>
				{p.level}
			</span>
		</span>
	);
}

function DamageBar({ p, maxDamage, small }: { p: ScoreboardPlayer; maxDamage: number; small?: boolean }) {
	return (
		<span className="block min-w-0 flex-1">
			<span className={cn("flex justify-between", small ? "text-[10px]" : "text-[11px]")}>
				<span className="text-ink">{p.damage.toLocaleString()}</span>
				<span className="text-ink-3">{p.damageShare}%</span>
			</span>
			<span className={cn("mt-[3px] block overflow-hidden rounded-[3px] bg-line", small ? "h-1" : "h-[5px]")}>
				<span className="block h-full rounded-[3px] bg-ink-2" style={{ width: `${maxDamage ? (p.damage / maxDamage) * 100 : 0}%` }} />
			</span>
		</span>
	);
}

function TeamTable({ team, side, region, me, maxDamage, v }: { v: string; team: ScoreboardTeam; side: "blue" | "red"; region: string; me: string; maxDamage: number }) {
	return (
		<div className="overflow-hidden rounded-lg border border-line bg-surface">
			<TeamHeader team={team} side={side} v={v} />

			{/* ≥ md: table */}
			<div className="hidden md:block">
				<div aria-hidden="true" className={cn("grid border-b border-line px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[.08em] text-ink-3", WIDE_COLS)}>
					<span>Player</span>
					<span>KDA</span>
					<span>Damage</span>
					<span className="text-right">CS</span>
					<span className="text-right">Gold</span>
					<span className="text-right">Vis.</span>
					<span>Items</span>
				</div>
				{team.players.map((p, i) => {
					const kda = kdaOf(p.kills, p.deaths, p.assists);
					const isMe = `${p.name}#${p.tagLine}`.toLowerCase() === me;
					return (
						<div key={i} aria-current={isMe || undefined} className={cn("grid items-center px-3 py-1.5 text-xs", WIDE_COLS, isMe && "bg-signal/[.07] shadow-[inset_2px_0_0_var(--color-signal)]")}>
							<span className="flex min-w-0 items-center gap-1.5">
								<ChampWithLevel p={p} size={32} v={v} />
								<span className="ml-0.5 flex flex-col gap-px">
									{[p.summoner1Id, p.summoner2Id].map((id, j) => (id ? <img key={j} src={getSummonerSpellUrl(id, v)} onError={ddFallback} alt={getSummonerSpellName(id)} className="block h-[15px] w-[15px] rounded-[3px]" /> : null))}
								</span>
								{p.keystone ? <img src={getRuneIconUrl(p.keystone)} alt={getRuneName(p.keystone)} className="h-[18px] w-[18px] rounded-full bg-[#07090d]" /> : null}
								<span className="ml-0.5 min-w-0">
									<PlayerName p={p} region={region} isMe={isMe} />
									<span className="block truncate text-[11px] text-ink-3">{p.championName}</span>
								</span>
							</span>
							<span>
								<span className="block whitespace-nowrap font-bold">
									{p.kills}/{p.deaths}/{p.assists}
								</span>
								<span className={cn("block text-[11px] font-semibold", kdaColor(kda))}>{formatKda(kda)}</span>
							</span>
							<DamageBar p={p} maxDamage={maxDamage} />
							<span className="text-right">{p.cs}</span>
							<span className="text-right">{k(p.gold)}</span>
							<span className="text-right">{p.visionScore}</span>
							<span className="flex gap-0.5">
								{p.items.slice(0, 7).map((id, j) => (
									<ItemIcon key={j} itemId={id} size="sm" isTrinket={j === 6} gameVersion={v} />
								))}
							</span>
						</div>
					);
				})}
			</div>

			{/* < md: two-line player cards, nothing scrolls sideways */}
			<ul className="m-0 list-none p-0 md:hidden">
				{team.players.map((p, i) => {
					const kda = kdaOf(p.kills, p.deaths, p.assists);
					const isMe = `${p.name}#${p.tagLine}`.toLowerCase() === me;
					return (
						<li key={i} aria-current={isMe || undefined} className={cn("flex flex-col gap-1.5 border-t border-[#1f2631] px-2.5 py-2", isMe && "bg-signal/[.07] shadow-[inset_2px_0_0_var(--color-signal)]")}>
							<div className="flex items-center gap-2">
								<ChampWithLevel p={p} size={34} v={v} />
								{p.keystone ? <img src={getRuneIconUrl(p.keystone)} alt={getRuneName(p.keystone)} className="ml-0.5 h-[18px] w-[18px] rounded-full bg-[#07090d]" /> : null}
								<span className="min-w-0 flex-1">
									<PlayerName p={p} region={region} isMe={isMe} className="text-[13px]" />
									<span className="block text-[11px] text-ink-3">
										{p.championName} · {p.cs} CS · {k(p.gold)}
									</span>
								</span>
								<span className="text-right">
									<span className="block whitespace-nowrap text-sm font-extrabold">
										{p.kills}/{p.deaths}/{p.assists}
									</span>
									<span className={cn("block text-[11px] font-semibold", kdaColor(kda))}>{formatKda(kda)}</span>
								</span>
							</div>
							<div className="flex items-center gap-2">
								<span className="flex gap-0.5">
									{p.items.slice(0, 7).map((id, j) => (
										<ItemIcon key={j} itemId={id} size="xs" isTrinket={j === 6} gameVersion={v} />
									))}
								</span>
								<DamageBar p={p} maxDamage={maxDamage} small />
							</div>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

export function ScoreboardSkeleton() {
	return (
		<div aria-busy="true" aria-label="Loading scoreboard" className="flex flex-col gap-[5px]">
			{Array.from({ length: 10 }, (_, i) => (
				<Skeleton key={i} className="h-[38px]" />
			))}
		</div>
	);
}

/** All ten players of a game. Loaded when a row is first expanded; finished games never change, so it is fetched once. */
export function ScoreboardPanel({ matchId, region, me }: { matchId: string; region: string; me: string }) {
	const [data, setData] = useState<Scoreboard | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setError(null);
		fetchScoreboard(matchId)
			.then(d => !cancelled && setData(d))
			.catch(e => !cancelled && setError(e instanceof ApiError ? e.message : "Could not load this game"));
		return () => {
			cancelled = true;
		};
	}, [matchId, attempt]);

	const retry = useCallback(() => setAttempt(a => a + 1), []);

	if (error)
		return (
			<div role="alert" className="flex flex-wrap items-center gap-3 px-1 py-3.5 text-sm text-ink-2">
				<span className="flex-[1_1_200px]">
					<span className="font-semibold text-ink">Couldn&apos;t load this game.</span> {error}. Finished games don&apos;t change, so it&apos;s safe to retry.
				</span>
				<button type="button" onClick={retry} className={button.secondary}>
					Try again
				</button>
			</div>
		);
	if (!data) return <ScoreboardSkeleton />;

	const maxDamage = Math.max(1, ...data.teams.flatMap(t => t.players.map(p => p.damage)));
	return (
		<div className="flex flex-col gap-2.5">
			<div className="flex justify-between gap-2 px-0.5 font-mono text-[11px] text-ink-3">
				<span>
					{getQueueLabel(data.queueId)} · {formatDuration(data.gameDuration)}
				</span>
				<span>patch {data.gameVersion.split(".").slice(0, 2).join(".")}</span>
			</div>
			{data.teams.map(t => (
				<TeamTable key={t.teamId} v={data.gameVersion} team={t} side={t.teamId === 100 ? "blue" : "red"} region={region} me={me} maxDamage={maxDamage} />
			))}
		</div>
	);
}
